import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDisclosure } from '@withjoy/joykit';
import { useEventUserRole } from '@shared/components/AuthProvider';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { isInIframe } from '@shared/utils/isInIframe';
import queryString from '@shared/core/queryString';
import { GuestIdentificationDialogContextProvider } from './GuestIdentificationDialog.context';
import { GuestIdentificationDialog } from './GuestIdentificationDialog';
import { GuestIdentificationStickyButton } from './GuestIdentificationStickyButton';
import { useGuestIdentificationDialogController } from './GuestIdentificationDialog.controller';
import { hasDismissedGuestIdentificationDialog, markGuestIdentificationDialogDismissed } from './guestIdentificationDismissal';
import type { GuestIdentificationStep } from '../GuestIdentification/GuestIdentification.types';

// null = dismissal state not loaded yet (SSR or before eventId is known).
// true/false = localStorage has been read for the current eventId.
// Distinguishing "unknown" from "not dismissed" prevents the dialog from
// briefly auto-opening on refresh before we've read the persisted flag.
type DismissalState = boolean | null;

const isPreviewing = queryString.getValueString('preview') === 'true';

/**
 * Top-level gate. The dialog is disabled when:
 * - the `joyProGuestIdentificationDialog` flag is off,
 * - the URL is in preview mode (`?preview=true`),
 * - the page is inside the admin website-editor iframe — narrow condition
 *   "admin viewing inside an iframe" — to avoid the eventByName eviction
 *   loop pulling our query into a refetch storm on localhost. Admins
 *   testing the guest site directly still see it; the dialog itself
 *   filters out admins-with-identity via canIdentifyGuest.
 *
 * Inside an iframe we additionally wait for auth to settle before enabling
 * the dialog. During the startup window `isAdmin` briefly reports false,
 * so without this guard the eligibility query would subscribe and get
 * pulled into the preview refetch storm before the admin check can
 * resolve. We scope this wait to iframe contexts only — incognito
 * localhost direct visits aren't in an iframe, and there
 * `hasIdentifiedUserOnce` can stay false indefinitely (cross-origin auth
 * probe to dev.withjoy.com).
 *
 * The inner provider is always rendered so the React tree shape stays
 * stable across gate transitions (notably the async flag resolution),
 * which would otherwise remount the entire guest-site subtree.
 */
export const GuestIdentificationDialogProvider: React.FC = ({ children }) => {
  const { isAdmin, hasIdentifiedUserOnce } = useEventUserRole();
  const insideIframe = isInIframe();
  const [isFeatureEnabled] = useIsFeatureEnabled('joyProGuestIdentificationDialog');

  const isEnabled = isFeatureEnabled && !isPreviewing && !(insideIframe && (!hasIdentifiedUserOnce || isAdmin));

  return <GuestIdentificationDialogProviderInner isEnabled={isEnabled}>{children}</GuestIdentificationDialogProviderInner>;
};

const GuestIdentificationDialogProviderInner: React.FC<{ isEnabled: boolean }> = ({ isEnabled, children }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [step, setStep] = useState<GuestIdentificationStep>('email');
  const [email, setEmail] = useState('');
  const [isDismissed, setIsDismissed] = useState<DismissalState>(null);
  const [hasIdentifiedThisSession, setHasIdentifiedThisSession] = useState(false);
  const { canIdentifyGuest, eventId } = useGuestIdentificationDialogController({ skip: !isEnabled });
  const hasAutoOpenedRef = useRef(false);

  // Read dismissal flag from localStorage once eventId is known. Stays null
  // during SSR and until the eligibility query resolves.
  useEffect(() => {
    if (!eventId) {
      setIsDismissed(null);
      return;
    }
    setIsDismissed(hasDismissedGuestIdentificationDialog(eventId));
  }, [eventId]);

  // Auto-open once per mount when the guest is eligible AND we've confirmed
  // they haven't dismissed it before (isDismissed === false, not null).
  useEffect(() => {
    if (!canIdentifyGuest || isDismissed !== false || hasAutoOpenedRef.current) return;
    hasAutoOpenedRef.current = true;
    onOpen();
  }, [canIdentifyGuest, isDismissed, onOpen]);

  const handleDismiss = useCallback(() => {
    if (!eventId) return;
    markGuestIdentificationDialogDismissed(eventId);
    setIsDismissed(true);
  }, [eventId]);

  // Distinguishes "user identified successfully" from "user dismissed". The
  // eligibility query refetches asynchronously after the OTP flow evicts
  // identity-dependent fields; without this signal, `canIdentifyGuest` stays
  // briefly true on the stale cache result and the sticky CTA flashes back
  // in. Cleared on next page load — eligibility will then resolve with
  // hasIdentity: true and canIdentifyGuest naturally becomes false.
  const handleIdentified = useCallback(() => {
    setHasIdentifiedThisSession(true);
  }, []);

  const handleStickyButtonClick = useCallback(() => {
    setStep('email');
    onOpen();
  }, [onOpen]);

  // Sticky CTA takes over once the guest has dismissed the dialog. On refresh
  // with a persisted dismissal, this renders directly (with its slide-in
  // animation) instead of the dialog re-appearing.
  const showStickyButton = canIdentifyGuest && isDismissed === true && !isOpen && !hasIdentifiedThisSession;

  return (
    <GuestIdentificationDialogContextProvider value={{ isOpen, onOpen, onClose, step, setStep, email, setEmail }}>
      {children}
      {isEnabled && (
        <>
          <GuestIdentificationDialog
            eventId={eventId}
            onDismiss={handleDismiss}
            onIdentified={handleIdentified}
          />
          {showStickyButton && <GuestIdentificationStickyButton onClick={handleStickyButtonClick} />}
        </>
      )}
    </GuestIdentificationDialogContextProvider>
  );
};
